import {
  APP_INITIALIZER,
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  provideZoneChangeDetection,
} from '@angular/core';
import { registerLocaleData } from '@angular/common';
import localeFr from '@angular/common/locales/fr';
import localeEn from '@angular/common/locales/en';
import { GlobalErrorHandler } from './core/error/global-error-handler';
import { RealtimeBootstrap } from './core/realtime/realtime-bootstrap.service';
import { PushBootstrap } from './core/push/push-bootstrap.service';
import {
  provideHttpClient,
  HttpClient,
  withInterceptors,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { jwtInterceptor } from './services/jwt.interceptor';
import { idempotencyInterceptor } from './services/idempotency.interceptor';
import { errorInterceptor } from './services/error.interceptor';
import { offlineInterceptor } from './core/offline/offline.interceptor';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { routes } from './app.routes';
import {
  provideRouter,
  withComponentInputBinding,
  withInMemoryScrolling,
} from '@angular/router';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideClientHydration } from '@angular/platform-browser';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { ToastrModule, provideToastr } from 'ngx-toastr';
import { TablerIconsModule } from 'angular-tabler-icons';
import * as TablerIcons from 'angular-tabler-icons/icons';
import { NgScrollbarModule } from 'ngx-scrollbar';
import { NgxPermissionsModule } from 'ngx-permissions';
import { MaterialModule } from './material.module';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CalendarModule, DateAdapter } from 'angular-calendar';
import { adapterFactory } from 'angular-calendar/date-adapters/date-fns';
import { provideHighlightOptions } from 'ngx-highlightjs';

/**
 * Loader composite : merge le fichier template historique (en, fr, de, es)
 * avec le fichier Dinki dédié ({@code dinki-{lang}.json}) qui contient les
 * traductions métier de l'application.
 *
 * <p>Les langues exclusives à Dinki (wo, bm, ha, dje) chargent uniquement
 * le fichier Dinki ; si une clé n'est pas trouvée dans la langue active,
 * ngx-translate retombe automatiquement sur la {@code defaultLang} (fr).</p>
 */
export class CustomLoader implements TranslateLoader {
  private readonly hasLegacy = new Set(['en', 'fr', 'de', 'es']);

  constructor(
    private readonly http: HttpClient,
    private readonly prefix: string,
    private readonly suffix: string,
  ) {}

  getTranslation(lang: string): Observable<any> {
    const dinki$ = this.http
      .get(`${this.prefix}dinki-${lang}${this.suffix}`)
      .pipe(catchError(() => of({})));

    if (!this.hasLegacy.has(lang)) {
      return dinki$;
    }

    const legacy$ = this.http
      .get(this.prefix + lang + this.suffix)
      .pipe(catchError(() => of({})));

    return forkJoin([legacy$, dinki$]).pipe(
      map(([legacy, dinki]) => ({
        ...(legacy as object),
        ...(dinki as object),
      })),
    );
  }
}

export function HttpLoaderFactory(http: HttpClient) {
  return new CustomLoader(http, './assets/i18n/', '.json');
}

// ─── Locales Angular (DatePipe / CurrencyPipe / DecimalPipe) ───────────────
//
// Enregistrées ICI, au chargement du module, et NON dans le constructeur de
// LocaleService : ce service est `providedIn: 'root'`, donc instancié
// paresseusement. Les routes qui ne l'injectent pas — typiquement l'espace
// client public `/c/:token`, dont le layout est autonome et dont
// jwt.interceptor évite volontairement LocaleService (dépendance circulaire) —
// rendaient leurs templates AVANT tout enregistrement, d'où :
//   RuntimeError NG0701: Missing locale data for the locale "fr"
// sur un pipe à locale explicite (`| currency:'XOF':'symbol':'1.0-0':'fr'`).
//
// Une exécution au niveau module est garantie avant le premier rendu, quelle
// que soit la route d'entrée. registerLocaleData est idempotent.
registerLocaleData(localeFr, 'fr');
registerLocaleData(localeEn, 'en');

export const appConfig: ApplicationConfig = {
  providers: [
    provideAnimationsAsync(),
    provideToastr(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    // Capture globale des erreurs JS non gérées (hors HTTP, déjà couvert par errorInterceptor).
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    provideHighlightOptions({
      coreLibraryLoader: () => import('highlight.js/lib/core'),
      lineNumbersLoader: () => import('ngx-highlightjs/line-numbers'),
      languages: {
        typescript: () => import('highlight.js/lib/languages/typescript'),
        css: () => import('highlight.js/lib/languages/css'),
        xml: () => import('highlight.js/lib/languages/xml'),
      },
    }),
    provideRouter(
      routes,
      withInMemoryScrolling({
        scrollPositionRestoration: 'enabled',
        anchorScrolling: 'enabled',
      }),
      withComponentInputBinding(),
    ),
    provideHttpClient(
      // Ordre : jwt → idempotency → offline → error.
      //  - jwt          : ajoute Authorization + Accept-Language.
      //  - idempotency  : pose un X-Idempotency-Key sur les POST/PUT/PATCH/DELETE
      //                   (ignoré si l'appelant en a déjà mis un, ex : mutation-queue).
      //                   DOIT être avant offline pour que la clé soit capturée
      //                   dans le snapshot de la queue en cas de mise en file.
      //  - offline      : cache GET, met en file les writes hors-ligne.
      //  - error        : toasts globaux et redirections sur erreurs HTTP.
      withInterceptors([jwtInterceptor, idempotencyInterceptor, offlineInterceptor, errorInterceptor]),
      withInterceptorsFromDi(),
    ),
    provideClientHydration(),
    // Démarre / arrête automatiquement le pont Supabase Realtime au login/logout.
    {
      provide: APP_INITIALIZER,
      multi: true,
      useFactory: (boot: RealtimeBootstrap) => () => boot.init(),
      deps: [RealtimeBootstrap],
    },
    // Enregistre le Service Worker Web Push dès le boot (silencieux si non supporté).
    {
      provide: APP_INITIALIZER,
      multi: true,
      useFactory: (boot: PushBootstrap) => () => boot.init(),
      deps: [PushBootstrap],
    },
    importProvidersFrom(
      FormsModule,
      ToastrModule.forRoot({
        positionClass: 'toast-top-right',
        timeOut: 6000,
        closeButton: true,
        progressBar: true,
        preventDuplicates: true,
        countDuplicates: true,
        newestOnTop: true,
      }),
      ReactiveFormsModule,
      MaterialModule,
      NgxPermissionsModule.forRoot(),
      TablerIconsModule.pick(TablerIcons),
      NgScrollbarModule,
      CalendarModule.forRoot({
        provide: DateAdapter,
        useFactory: adapterFactory,
      }),
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient],
        },
      }),
    ),
  ],
};
